import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, UseMutationResult } from 'react-query';
import { client } from 'utils/api-client';

interface TrackingCsvParams {
  month: string;
  programStatus?: string;
}

interface ApiError {
  message?: string;
}

interface TrackingCsvResult {
  blob: Blob;
  filename: string;
}

interface TrackingCsvError extends Error {
  response?: { data: ApiError };
}

const parseErrorBlob = async (blob: Blob): Promise<ApiError> => {
  try {
    const text = await blob.text();
    return JSON.parse(text) as ApiError;
  } catch {
    return { message: 'An error occurred while generating the export.' };
  }
};

const extractFilenameFromHeader = (contentDisposition: string | undefined): string | null => {
  if (!contentDisposition) return null;
  const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
  return filenameMatch ? filenameMatch[1] : null;
};

const trackingCsvExport = async (params: TrackingCsvParams): Promise<TrackingCsvResult> => {
  const apiParams: Record<string, string> = { month: params.month };
  if (params.programStatus) apiParams.program_status = params.programStatus;

  try {
    const response: AxiosResponse<Blob> = await client.get('check-in-program/tracking/csv/', {
      params: apiParams,
      responseType: 'blob',
    });
    const filename =
      extractFilenameFromHeader(response.headers['content-disposition']) ||
      `challenges-tracking-${params.month}.csv`;
    return { blob: response.data, filename };
  } catch (error) {
    const axiosError = error as AxiosError<Blob>;
    if (axiosError.response?.data instanceof Blob) {
      const parsedError = await parseErrorBlob(axiosError.response.data);
      const enhancedError: TrackingCsvError = new Error(parsedError.message);
      enhancedError.response = { data: parsedError };
      throw enhancedError;
    }
    throw error;
  }
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const useCommittedClubTrackingCsv = (): UseMutationResult<
  TrackingCsvResult,
  TrackingCsvError,
  TrackingCsvParams
> => {
  return useMutation({
    mutationFn: trackingCsvExport,
    onSuccess: data => {
      downloadBlob(data.blob, data.filename);
    },
  });
};

export type { TrackingCsvError };
export default useCommittedClubTrackingCsv;
